import React, { useEffect, useState } from "react";
import { getOccasionQuestionSet } from "../../lib/api";
import { useDraft } from "../../lib/draft-store";
import { SkeletonCard } from "../shared/Skeleton";

/**
 * Step 3 — Conditional scope questions.
 * Fetches the occasion question set and renders each question by its `type`.
 *
 * Supported types:
 *   - single_choice  → radio chips
 *   - number_chip    → chip-style number picker
 *   - multi_select   → checkbox chips
 *   - number         → <input type=number>
 *   - text           → <input type=text>
 *   - textarea       → <textarea>
 *   - boolean        → Yes / No / Depends chips
 */
export default function Step3Questions() {
  const { payload, setField } = useDraft();
  const occasion = payload?.occasion;
  const [qs, setQs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!occasion) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await getOccasionQuestionSet(occasion);
        if (!cancelled) setQs(data);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [occasion]);

  if (!occasion) {
    return (
      <div data-testid="plan-step-3">
        <p className="text-sm text-[#1A1A1A]/65">
          Pick an occasion in Step 1 first.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div data-testid="plan-step-3" className="space-y-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error || !qs) {
    return (
      <div data-testid="plan-step-3">
        <p className="text-sm text-[#a33]">
          Couldn't load questions. Please go back and re-select your occasion.
        </p>
      </div>
    );
  }

  const scope = payload?.scope || {};
  const setAns = (id, value) => setField(["scope", id], value);

  return (
    <div data-testid="plan-step-3">
      <h1 className="font-display text-3xl text-[#1A1A1A] sm:text-4xl">
        {qs.title || "Tell us more"}
      </h1>
      <p className="mt-3 max-w-xl text-base text-[#1A1A1A]/65">
        A few quick details so we can size up the crew & deliverables.
      </p>

      <ol className="mt-8 space-y-8">
        {(qs.questions || [])
          .filter(
            (q) =>
              !["location", "date", "city"].includes(q.type) &&
              !["location_count", "destination", "city", "event_date"].includes(
                q.id,
              ),
          )
          .map((q) => {
          const value = scope[q.id];
          return (
            <li
              key={q.id}
              data-testid={`question-${q.id}`}
              className="rounded-xl border border-[#EAE3D2] bg-white p-5 sm:p-6"
            >
              <p className="font-display text-lg text-[#1A1A1A]">
                {q.label}
                {q.required ? (
                  <span className="ml-1 text-[#C8A24B]" aria-label="required">*</span>
                ) : null}
              </p>
              {q.help_text ? (
                <p className="mt-1 text-xs text-[#1A1A1A]/55">{q.help_text}</p>
              ) : null}
              <div className="mt-4">
                {renderInput(q, value, (v) => setAns(q.id, v))}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function renderInput(q, value, onChange) {
  switch (q.type) {
    case "single_choice":
    case "single_select":
      return (
        <div role="radiogroup" className="flex flex-wrap gap-2">
          {(q.options || []).map((o) => {
            const active = value === o.value;
            return (
              <button
                key={String(o.value)}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`q-${q.id}-opt-${o.value}`}
                onClick={() => onChange(o.value)}
                className={chipCls(active)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    case "number_chip":
      return (
        <div role="radiogroup" className="flex flex-wrap gap-2">
          {(q.options || []).map((o) => {
            const active = value === o.value;
            return (
              <button
                key={String(o.value)}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`q-${q.id}-opt-${o.value}`}
                onClick={() => onChange(o.value)}
                className={chipCls(active)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    case "multi_select":
    case "multi_choice": {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="flex flex-wrap gap-2">
          {(q.options || []).map((o) => {
            const active = arr.includes(o.value);
            return (
              <button
                key={String(o.value)}
                type="button"
                aria-pressed={active}
                data-testid={`q-${q.id}-opt-${o.value}`}
                onClick={() => {
                  const next = active
                    ? arr.filter((v) => v !== o.value)
                    : [...arr, o.value];
                  onChange(next);
                }}
                className={chipCls(active)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    case "boolean": {
      const opts = [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "depends", label: "Depends" },
      ];
      return (
        <div role="radiogroup" className="flex gap-2">
          {opts.map((o) => {
            const active = value === o.value;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`q-${q.id}-opt-${o.value}`}
                onClick={() => onChange(o.value)}
                className={chipCls(active)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    case "number":
      return (
        <input
          type="number"
          data-testid={`q-${q.id}-input`}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          className="w-full max-w-xs rounded-lg border border-[#EAE3D2] bg-white px-4 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
        />
      );
    case "textarea":
      return (
        <textarea
          data-testid={`q-${q.id}-input`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
        />
      );
    case "text":
    default:
      return (
        <input
          type="text"
          data-testid={`q-${q.id}-input`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full max-w-md rounded-lg border border-[#EAE3D2] bg-white px-4 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
        />
      );
  }
}

function chipCls(active) {
  return [
    "rounded-full border px-4 py-2 text-xs uppercase tracking-[0.16em] transition",
    active
      ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF7F2]"
      : "border-[#1A1A1A]/15 text-[#1A1A1A]/80 hover:border-[#C8A24B] hover:text-[#C8A24B]",
  ].join(" ");
}

export function step3IsValid(payload, requiredIds = []) {
  if (!requiredIds.length) return true;
  const scope = payload?.scope || {};
  return requiredIds.every((id) => {
    const v = scope[id];
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && v !== "";
  });
}
